import { FC, useCallback, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormikProps } from 'formik';

import CustomInput from '../../../../../../../common/CustomInput';
import FileUploader from '../../../../../../../common/FileUploader';
import { CreateActivityFormData } from '../../../../../../../types/activity.types';
import { useAppSelector, useAppDispatch } from '../../../../../../../hooks/redux';
import { useGetTagsQuery } from '../../../../../../../store/services/tagsApi';
import { setTags, Tag } from '../../../../../../../store/slices/tagsSlice';

import styles from './DocumentsSection.module.scss';

interface DocumentsSectionProps {
  formik: FormikProps<CreateActivityFormData>;
}

const DocumentsSection: FC<DocumentsSectionProps> = ({ formik }) => {
  const MAX_DOCUMENT_TAGS = 5;
  const dispatch = useAppDispatch();
  const { values, setFieldValue, errors, touched } = formik;
  const { tags } = useAppSelector(state => state.tags);
  const currentTags = tags ?? [];
  const { data: tagsRes } = useGetTagsQuery({});

  useEffect(() => {
    if (!tagsRes) return;

    const isSameTags =
      tagsRes.length === currentTags.length &&
      tagsRes.every((tag: Tag, index: number) => {
        const currentTag = currentTags[index];
        return currentTag && currentTag.id === tag.id && currentTag.name === tag.name && currentTag.color === tag.color;
      });

    if (!isSameTags) {
      dispatch(setTags(tagsRes));
    }
  }, [tagsRes, currentTags, dispatch]);

  const handleFilesChange = useCallback(
    (files: File[]) => {
      setFieldValue('attachments', files);
    },
    [setFieldValue]
  );

  const handleRemoveFile = useCallback(
    (fileIndex: number) => {
      const updatedFiles = values.attachments.filter((_, index) => index !== fileIndex);
      setFieldValue('attachments', updatedFiles);
    },
    [values.attachments, setFieldValue]
  );

  const handleTagToggle = (tag: Tag) => {
    const isSelected = values.documentTags.some(t => t.id === tag.id);

    if (isSelected) {
      const updatedTags = values.documentTags.filter(t => t.id !== tag.id);
      setFieldValue('documentTags', updatedTags);
    } else {
      if (values.documentTags.length >= MAX_DOCUMENT_TAGS) {
        return;
      }
      const updatedTags = [...values.documentTags, tag];
      setFieldValue('documentTags', updatedTags);
    }
  };

  const getError = (fieldName: keyof CreateActivityFormData) => {
    return touched[fieldName] && errors[fieldName] ? String(errors[fieldName]) : '';
  };

  return (
    <Box className={styles.documentsSection}>
      <CustomInput
        inputName="documentName"
        inputLabel="Name"
        value={values.documentName}
        onChange={formik.handleChange}
        error={getError('documentName')}
      />
      <Box className={styles.tagsSection}>
        <Typography
          variant="h11Semibold"
          className={styles.sectionLabel}
        >
          Tags
        </Typography>

        <Box className={styles.tagsContainer}>
          {tags && tags.length > 0 ? (
            tags.map(tag => {
              const isSelected = values.documentTags.some(t => t.id === tag.id);

              return (
                <Box
                  key={tag.id}
                  className={`${styles.tagItem} ${isSelected ? styles.selected : ''}`}
                  onClick={() => handleTagToggle(tag)}
                >
                  <Typography
                    variant="h12Semibold"
                    className={styles.tagText}
                    sx={{
                      backgroundColor: tag.color,
                      color: '#222B3A',
                    }}
                  >
                    {tag.name}
                  </Typography>
                </Box>
              );
            })
          ) : (
            <Typography
              variant="h12"
              sx={{ color: '#5E6A81' }}
            >
              No tags available
            </Typography>
          )}
        </Box>
        {values.documentTags.length >= MAX_DOCUMENT_TAGS && (
          <Typography
            variant="h13"
            sx={{ color: '#5E6A81', mt: 1 }}
          >
            Up to {MAX_DOCUMENT_TAGS} tags per document
          </Typography>
        )}
        {values.documentTags.length > 0 && (
          <Box className={styles.selectedTagsContainer}>
            <Typography
              variant="h12"
              sx={{ color: '#5E6A81', mb: 1 }}
            >
              Selected tags:
            </Typography>
            <Box className={styles.selectedTags}>
              {values.documentTags.map(tag => (
                <Box
                  key={tag.id}
                  className={styles.selectedTag}
                  sx={{
                    backgroundColor: tag.color,
                    color: '#222B3A',
                  }}
                >
                  <Typography variant="h12Semibold">{tag.name}</Typography>
                  <button
                    type="button"
                    className={styles.removeTagButton}
                    onClick={() => handleTagToggle(tag)}
                    aria-label={`Remove tag ${tag.name}`}
                  >
                    <CloseIcon fontSize="inherit" />
                  </button>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <Box className={styles.uploadSection}>
        <Typography
          variant="h11Semibold"
          className={styles.sectionLabel}
        >
          Attach Documents
        </Typography>

        <FileUploader
          files={values.attachments}
          onFilesChange={handleFilesChange}
          onRemoveFile={handleRemoveFile}
          placeholder={{
            title: 'Select your file or drag and drop',
            subtitle: 'png, pdf, jpg, docx accepted',
          }}
        />
      </Box>
    </Box>
  );
};

export default DocumentsSection;
